import React, { useState, useEffect } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
} from "@mui/material";
import { useRootStore } from "../../../../store/contexts";
import { getApiAsync } from "../../../../api";
import { MusicalWork, SortOrder } from "../../../../api/graphql";

type MusicalWorkPanelProps = { parentId: string; refIds: string[] };

export const MusicalWorkPanel: React.FC<MusicalWorkPanelProps> = ({
  parentId,
  refIds,
}) => {
  const rootStore = useRootStore();
  // Extraídos del rootStore para que ESLint vea la cadena de deps del useEffect.
  const { langStore, authStore, activityStore } = rootStore;
  const [items, setItems] = useState<MusicalWork[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const loadData = async () => {
      try {
        if (cancelled) return;
        const api = await getApiAsync(authStore, activityStore);
        if (refIds.length === 0) {
          setItems([]);
          setLoading(false);
          return;
        }
        const result = await api.musicalWorkListWithOffset({
          where: { id: { in: refIds } },
          orderBy: [{ lastUpdatedAt_: SortOrder.Desc }],
          take: 100,
          offset: 0,
        });
        if (!cancelled) {
          setItems(result.musicalWorkListWithOffset as MusicalWork[]);
        }
      } catch (error) {
        console.error("Failed to load musicalWork:", error);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadData();
    return () => {
      cancelled = true;
    };
  }, [parentId, refIds, authStore, activityStore]);

  return (
    <Box sx={{ mb: 2 }}>
      <Typography
        variant="subtitle2"
        sx={{ mb: 1, display: "flex", alignItems: "center", gap: 1 }}
      >
        {langStore.MusicalWorkLabels?.p || "MusicalWork"}
        {loading && <CircularProgress size={16} />}
        {!loading && items.length === 0 && (
          <Typography component="span" variant="body2" color="text.secondary">
            ({langStore.noRecordsMessage})
          </Typography>
        )}
      </Typography>
      {!loading && items.length > 0 && (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>
                {langStore.MusicalWorkTitleLabel || "title"}
              </TableCell>
              <TableCell>
                {langStore.MusicalWorkMusicalGenreLabel || "musicalGenre"}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((item) => (
              <React.Fragment key={item.id}>
                <TableRow
                  hover
                  sx={{ cursor: "pointer" }}
                  onClick={(e) => {
                    e.stopPropagation();
                    rootStore.openDrawerView("musicalWork", item.id);
                  }}
                >
                  <TableCell>{String(item.title ?? "-")}</TableCell>
                  <TableCell>{item.musicalGenre?.displayId || "-"}</TableCell>
                </TableRow>
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      )}
    </Box>
  );
};
